import React, { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Box } from '@wds/box';
import { BasePage, Helmet } from '@sm/webassets';
import { Link } from '@wds/link';
import { Typography } from '@wds/typography';
import { Button } from '@wds/button';
import { MENU_PLACEMENTS, Menu } from '@wds/menu';
import { IconArrowLeft, IconMore } from '@wds/icons';
import ModernHeader from '../../components/ModernHeader';
import LeftNavLayout from '../../components/Layouts/LeftNavLayout';
import useStyles from './styles';
import installedApplications from './myApps.graphql';
import uninstallAppMutation from '../DetailsV2/remove-app.graphql';
import UninstallConfirmModal from '../DetailsV2/components/UninstallConfirmModal';

export const MyAppsV2Page = () => {
  const { homeContainer, bodyContainer, installedAppCard, installedAppCardTag } = useStyles();

  const [installedApps, setInstalledApps] = useState([]);
  const [appToUninstall, setAppToUninstall] = useState();
  const [confirmDialogVisible, setConfirmDialogVisible] = useState(false);

  const {
    data: queryResponseData,
    loading,
    refetch,
  } = useQuery(installedApplications, {
    variables: { language: 'en', page: 1, pageSize: 50 },
  });

  useEffect(() => {
    const {
      installedApplications: { items = [] },
    } = queryResponseData;
    setInstalledApps([...items]);
  }, [queryResponseData]);

  const [uninstallApp, { loading: uninstalling }] = useMutation(uninstallAppMutation, {
    onCompleted: () => {
      setConfirmDialogVisible(false);
      setAppToUninstall();
      refetch();
    },
  });

  const handleUninstallCancel = () => {
    setConfirmDialogVisible(false);
  };

  const handleUninstallConfirm = () => {
    uninstallApp({
      variables: { input: { appId: appToUninstall } },
    });
  };

  return (
    <div className={homeContainer}>
      <BasePage pageId="App Directory - My App" includeHeader={false} pageMetricsAttributes={null}>
        <Helmet>
          <title>SurveyMonkey Installed Apps</title>
        </Helmet>
        <ModernHeader v2="true" />
        <Box className={bodyContainer}>
          <LeftNavLayout
            nav={
              <Box>
                <Link underline={false} href="/apps">
                  <IconArrowLeft /> Back
                </Link>
              </Box>
            }
          >
            <>
              <Box mb={4}>
                <Typography variant="pageTitle" weight="medium">
                  Installed
                </Typography>
              </Box>
              <If condition={!loading}>
                <If condition={installedApps.length}>
                  {installedApps.map(app => (
                    <Box
                      key={app.id}
                      display="grid"
                      gridTemplateColumns="50px 1fr 4fr 150px"
                      mb={4}
                      className={installedAppCard}
                    >
                      <Box display="grid" placeItems="center">
                        <img src={app.logo} alt={`${app.name} Logo`} width="50" />
                      </Box>
                      <Box display="grid" placeItems="center" px={3}>
                        <Typography weight="medium">{app.name}</Typography>
                      </Box>
                      <Box display="grid" placeItems="center" justifyContent="baseline" px={3}>
                        <Box className={installedAppCardTag}>{app.tagline}</Box>
                      </Box>
                      <Box display="grid" placeItems="center">
                        <Box>
                          <Button color="alt" href={app.links.launch || app.links.detail}>
                            Open
                          </Button>

                          <Menu
                            placement={MENU_PLACEMENTS[0]}
                            menuTrigger={
                              <Button label="more" color="alt" variant="text">
                                <IconMore title="More" />
                              </Button>
                            }
                          >
                            <Menu.Item
                              onClick={() => {
                                setAppToUninstall(app.id);
                                setConfirmDialogVisible(true);
                              }}
                            >
                              Remove
                            </Menu.Item>
                          </Menu>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </If>
                <If condition={installedApps.length === 0}>
                  <Box mb={4} className={installedAppCard}>
                    <Typography>You currently have no installed apps</Typography>
                  </Box>
                </If>
              </If>
              <UninstallConfirmModal
                showModal={confirmDialogVisible}
                handleCancel={handleUninstallCancel}
                handleConfirm={handleUninstallConfirm}
                loading={uninstalling}
              />
            </>
          </LeftNavLayout>
        </Box>
      </BasePage>
    </div>
  );
};

export default MyAppsV2Page;
